import React, { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { FeaturedPreviewItem, FeaturedRelatedVideo } from '../../config/featuredTemplates';
import { useFeaturedPreviewStore } from '../../stores/featuredPreviewStore';
import { useAuthStore } from '../../stores/authStore';
import { HOME_TOPBAR_H } from '../home/layout/HomeTopBar';
import { FullscreenVideoPlayer } from './FullscreenVideoPlayer';
import { ReportDialog } from './ReportDialog';

/**
 * 视频作品预览（设计稿 202:501）——即梦式三段流式布局，无整页缩放
 * 渲染在 FeaturedPreviewPage 的流式内容区（顶栏下方、侧边栏右缘 → 视口右缘/下缘）
 * 内，根容器 h-full 随内容区弹性伸缩，元素尺寸恒定（字号不缩放）：
 * - 视频区：左缘贴 24、右缘距右栏 24（right 376 = 右栏 352 + 间距 24）双锚定，
 *   宽度随窗口自适应撑满；高度上下锚定（顶 = 视口 24、上探至顶栏下方，顶栏悬浮
 *   其上 / 底距 172 = 窗口条间距 24 + 缩略图条 124 + 底距 24，底部预留与顶部 24
 *   对称的空隙），随窗口高度
 *   弹性伸缩（rounded 16 + autoPlay + muted + loop；@1920×1080 = 1280x884）；窗口
 *   底部内部叠加 160px 多段缓动渐隐（压暗不到实色，画面朦胧
 *   透出保留窗口底部圆角轮廓）；画面 object-contain 完整显示不裁切（窗口比例弹性变化，比例差由
 *   黑边吸收，与图像预览页大图 contain 同哲学）
 * - 缩略图条：同宽双锚定、贴底 24、高 124 固定（220x124 间距 12 恒定不缩放，
 *   选中白描边 4、未选叠加 rgba(18,18,18,0.45)；悬停任意项取消压暗并底部浮现
 *   渐变+片名）；数据源双模式：精选入口 = 当前作品 + related 相关视频（related
 *   已含除当前外全部模板视频，点选仅本地切换播放源），作品入口 = 传入 items 的
 *   全部已发布视频作品（用户视频无 related；点选经 openWork + URL 同步切换
 *   整个作品，右栏信息随之更新，与图像预览页同款）；条按设计稿 202:505 固定
 *   尺寸横向滚动：内容溢出时两端 180px 渐隐遮罩 + 36x36 左右箭头循环切换，
 *   未溢出时格子靠左完整平铺（遮罩/箭头隐藏，不遮挡内容）
 * - 视频静音自动播放、底部无控制条（设计稿 202:501）；悬停浮现 45% 遮罩
 *   （#121212 @45%，设计稿 251:171）+ 中央 72x72 播放按钮（btn-play.svg），
 *   点击按钮全屏播放（FullscreenVideoPlayer，从当前进度续播出声）
 * - 右栏 352 宽贴右、全高（背景上探至视口顶，顶部不预留用户栏位）bg #181818，设计稿
 *   251:160（352 容器 + 288 内容 @32）：头像 20 / 用户名 14px / 关注 48x24 文字 10px /
 *   点赞 20 + 数字 14px Medium；标题 12px Light、日期行 12px；描述标签 14px、正文 14px
 *   行高 24 justified；参数 12px 底座 16x16；按钮 288x48 文字 16px Medium；flex 纵向布局：
 *   顶部 作者@栏内14 → 标题@68 固定，描述及以下弹性滚动（视频无图集行，直接跟标题）——
 *   描述、参数与按钮在滚动区按文档流顺排：参数距描述底 16、按钮距参数 32（251:160
 *   锚点节奏：描述底 454 → 参数 470 → 按钮 518），位置随描述长短动态变化、不贴底
 *   固定；内容超高时滚动区纵向滚动；用户栏在页面壳层 FeaturedPreviewPage
 * - 右栏可折叠（与图像预览页同款）：左缘 36x36 圆角 12 手柄（top-8），收起时右栏
 *   translate-x-full 滑出、视频区/缩略图条右缘 504→24 平滑扩展补位，手柄留在
 *   屏幕右缘、箭头翻转（收起 → / 展开 ←），300ms 同步过渡
 */
export const FeaturedVideoPreview: React.FC<{
  item: FeaturedPreviewItem;
  /** 全部已发布视频作品（作品入口底部切换条数据源，与 Bong 作品页同源同序；
      当前作品自带 related 时为精选模式，本参数不参与播放列表构建） */
  items?: FeaturedPreviewItem[];
  /** 点击「查看制作流程」：打开当前作品的只读画布项目（由 AppNew 传入） */
  onOpenWorkflow?: (item: FeaturedPreviewItem) => void;
}> = ({ item, items, onOpenWorkflow }) => {
  const openWork = useFeaturedPreviewStore((s) => s.open);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [followed, setFollowed] = useState(true);
  /* 右栏折叠：收起后右栏整体滑出右缘，视频区/缩略图条右缘扩展到屏幕边缘
     （与图像预览页同款交互，保持两页一致） */
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  /* 举报入口（方案 3.4）：仅用户发布的真实作品可举报（wf-/v- 前缀 = 模板/演示卡）；
     未登录先弹登录框，登录成功后经 pendingAction 回到举报弹层（与图像预览页同款） */
  const [reportOpen, setReportOpen] = useState(false);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const openLoginModal = useAuthStore((s) => s.openLoginModal);
  const openReport = () => {
    if (!isLoggedIn) {
      openLoginModal(() => setReportOpen(true));
      return;
    }
    setReportOpen(true);
  };

  /* 播放列表双模式（底部缩略图条数据源）：精选入口（模板视频自带 related）=
     当前作品 + 相关视频，点缩略图仅本地切换播放源；作品入口（用户视频无
     related）= items 全部已发布视频作品，点缩略图切换整个作品（右栏信息与
     URL 随之更新，刷新/直链可恢复）；两入口视觉交互完全一致（设计稿 202:505） */
  const worksMode = !(item.related && item.related.length) && !!items?.length;
  const playlist: FeaturedRelatedVideo[] = worksMode
    ? (items || []).map((w) => ({ id: w.id, title: w.title, cover: w.coverUrl || '', videoUrl: w.url, sources: w.sources }))
    : [
        { id: item.id, title: item.title, cover: item.coverUrl || '', videoUrl: item.url, sources: item.sources },
        ...(item.related || []),
      ];
  const [localIdx, setLocalIdx] = useState(0);
  /* 选中索引：作品模式跟随当前作品（store 切换驱动派生），精选模式本地状态驱动 */
  const activeIdx = worksMode
    ? Math.max(playlist.findIndex((v) => v.id === item.id), 0)
    : localIdx;
  const active = playlist[activeIdx] || playlist[0];

  /* 切换作品 = open 新作品 + 同步地址栏 query（replaceState 不触发 popstate 无循环），
     保证刷新/直链恢复的就是当前正在看的作品（与图像预览页同款） */
  const switchWork = (next: FeaturedPreviewItem) => {
    openWork(next);
    const url = new URL(window.location.href);
    url.searchParams.set('type', next.type);
    url.searchParams.set('id', next.id);
    window.history.replaceState(null, '', url.toString());
  };

  /* 点缩略图：作品模式切换整个作品（同一作品不重复 open），精选模式本地切播放源 */
  const selectStrip = (i: number) => {
    if (!worksMode) {
      setLocalIdx(i);
      return;
    }
    const next = (items || []).find((w) => w.id === playlist[i]?.id);
    if (next && next.id !== item.id) switchWork(next);
  };

  /* 切换视频后从头自动无声播放（key 重建 video 元素后兜底调 play） */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setProgress(0);
    video.play().catch(() => {});
  }, [activeIdx]);

  /* 缩略图条：横向滚动容器（设计稿 202:505 固定尺寸滚动模式） */
  const stripRef = useRef<HTMLDivElement>(null);

  /* 溢出检测：格 220x124 恒定不缩放（设计稿 202:505），总宽超出条宽才滚动并
     显示两端渐隐遮罩 + 左右箭头；未溢出时格子靠左完整平铺（遮罩/箭头隐藏，
     不遮挡内容）；ResizeObserver 随窗口/右栏折叠流式重算，列表变化后重测 */
  const [stripOverflow, setStripOverflow] = useState(false);
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const update = () => setStripOverflow(el.scrollWidth > el.clientWidth + 1);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [playlist.length]);

  /* 选中项滚入可视区（仅溢出滚动模式）：点缩略图或箭头切换后把被裁的选中格
     平滑滚入（与图片预览页图集行同款逻辑，步长 = 格宽 220 + 间距 12） */
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || !stripOverflow) return;
    const left = activeIdx * (220 + 12);
    const right = left + 220;
    let target = strip.scrollLeft;
    if (left < strip.scrollLeft) target = left;
    else if (right > strip.scrollLeft + strip.clientWidth) target = right - strip.clientWidth;
    if (target !== strip.scrollLeft) strip.scrollTo({ left: target, behavior: 'smooth' });
  }, [activeIdx, stripOverflow]);

  /* 左右箭头：循环步进播放列表（与图片预览页图集行箭头同款交互） */
  const step = (delta: number) => {
    selectStrip((activeIdx + delta + playlist.length) % playlist.length);
  };

  /* 悬停播放按钮 → 全屏播放：记录当前进度作续播起点并暂停预览视频 */
  const openFullscreen = () => {
    videoRef.current?.pause();
    setFullscreen(true);
  };

  return (
    <div className="relative h-full w-full bg-[#121212]">
      {/* 视频区：左右双锚定（left 24 / right 376 = 右栏 352 + 间距 24）宽度自适应撑满，上下锚定
          （top 视口 24 = 内容区内 -48、上探至顶栏下方，顶栏 z-[200] 悬浮其上，设计稿
          202:504 窗口 @ (264,24)；bottom 172 = 窗口条间距 24 + 缩略图条 124 + 底距 24，
          底部预留与顶部 24 对称的空隙）高度弹性伸缩（@1920×1080 = 1280x884 圆角 16）：
          悬停浮现遮罩+播放按钮，任何窗口尺寸下视频列完整贴合「左缘 → 右栏左缘 */}
      <div
        className={`group absolute bottom-[172px] left-6 overflow-hidden rounded-2xl bg-black transition-[right] duration-300 ease-out ${
          panelCollapsed ? 'right-6' : 'right-[376px]'
        }`}
        style={{ top: -HOME_TOPBAR_H + 24 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <video
          key={active.id}
          ref={videoRef}
          src={active.videoUrl}
          poster={active.cover || undefined}
          autoPlay
          muted
          loop
          playsInline
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
          /* object-contain 完整显示不裁切：窗口宽高比是弹性的（随窗口/右栏折叠流式
             变化），cover 会按窗口比例裁掉画面（折叠变宽后裁得更多）；contain 与图像
             预览页大图同哲学——内容等比完整呈现，比例差由黑边吸收（bg-black 融合） */
          className="h-full w-full object-contain"
        />
        {/* 窗口底部渐隐（设计稿 443:843 意图，改挂窗口内部）：贴底 160px 长区间渐变，
            三段色标缓动（0 透明 → 42% 处 0.35 → 底缘 0.72，顶部缓起中段渐深）比单段
            线性更柔和；终点压暗不到实色——画面朦胧透出，窗口底部圆角轮廓隐约可辨
            （若全实色则与页面底色融为一体、轮廓消失）；挂窗口内随 rounded-2xl 裁切、
            随右栏折叠/窗口伸缩自动联动；不设 z 值，位于悬停遮罩（z-10）/播放按钮
            （z-20）之下，不挡交互 */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[160px]"
          style={{
            background:
              'linear-gradient(180deg, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 0.35) 42%, rgba(18, 18, 18, 0.72) 100%)',
          }}
        />
        {/* 悬停 45% 遮罩（#121212 @45%，设计稿 251:171「4:3_遮罩」）：预览静音自动播放、
            无底部控制条，悬停时遮罩压暗画面提示可点击进入全屏 */}
        <div
          className={`absolute inset-0 z-10 transition-opacity duration-200 ${
            isHovered ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          style={{ backgroundColor: 'rgba(18, 18, 18, 0.45)' }}
        />
        {/* 悬停中央播放按钮（设计稿 251:171 btn_BF 72x72：白 65% 圆底 + 2px 白描边 +
            白三角）：点击全屏播放（FullscreenVideoPlayer，从当前进度续播出声） */}
        <button
          type="button"
          onClick={openFullscreen}
          aria-label="全屏播放"
          className={`absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-opacity duration-200 hover:opacity-90 ${
            isHovered ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img src="/icons/featured/btn-play.svg" alt="" className="h-[72px] w-[72px]" />
        </button>
      </div>

      {/* 底部缩略图条（切换作品选择栏）：同宽双锚定（left 24 / right 376/折叠后 24）、
          贴底 24、高 124 固定，设计稿 202:505——精选入口为相关视频（点选切播放源）、
          作品入口为全部已发布视频作品（点选切换整个作品，见播放列表双模式注释）；
          图格 220x124 间距 12 恒定不缩放（设计稿尺寸），总宽超出条宽时横向滚动：
          选中白描边 4、未选叠加 rgba(18,18,18,0.45)（悬停取消压暗）；悬停任意项
          底部浮现「整卡渐变 68%→91% + 48px 渐变条 + 片名」；两端 180px 页面底色
          渐隐遮罩（遮罩不挡滚动/点击）；左右箭头 36x36 垂直居中叠于遮罩之上，
          循环步进播放列表；未溢出时格子靠左完整平铺（遮罩/箭头隐藏不遮挡内容） */}
      <div
        className={`absolute bottom-6 left-6 z-20 h-[124px] transition-[right] duration-300 ease-out ${
          panelCollapsed ? 'right-6' : 'right-[376px]'
        }`}
      >
        <div
          ref={stripRef}
          className="no-scrollbar flex h-full w-full items-center gap-3 overflow-x-auto"
        >
          {playlist.map((v, i) => (
            <button
              key={v.id}
              type="button"
              onClick={() => selectStrip(i)}
              className="group relative flex-shrink-0 cursor-pointer overflow-hidden rounded-2xl"
              style={{ width: 220, height: 124 }}
            >
              {/* 封面兜底：无封面记录（旧数据/首帧抓取失败）用 <video #t=0.1> 加载首帧显示，
                  避免 <img src=""> 黑块露出 alt 文字；preload=metadata 只拉头部数据不开播 */}
              {v.cover ? (
                <img src={v.cover} alt={v.title} className="h-full w-full object-cover" />
              ) : (
                <video
                  src={`${v.videoUrl}#t=0.1`}
                  preload="metadata"
                  muted
                  playsInline
                  aria-hidden
                  tabIndex={-1}
                  className="pointer-events-none h-full w-full object-cover"
                />
              )}
              {/* 未选压暗层：悬停淡出（设计稿悬停项无 45% 遮罩，悬停提亮） */}
              {i !== activeIdx && (
                <span
                  className="absolute inset-0 transition-opacity duration-200 group-hover:opacity-0"
                  style={{ backgroundColor: 'rgba(18, 18, 18, 0.45)' }}
                />
              )}
              {/* 悬停态：整卡底部渐变（68% 透明 → 91% 实色）+ 底部 48px 渐变条 + 片名 */}
              <span
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                style={{ background: 'linear-gradient(180deg, rgba(20, 20, 20, 0) 68%, rgba(20, 20, 20, 1) 91%)' }}
              />
              <span
                className="pointer-events-none absolute bottom-0 left-0 h-12 w-full opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                style={{
                  background: 'linear-gradient(180deg, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 1) 100%)',
                  borderRadius: '0px 0px 16px 16px',
                }}
              />
              <span className="pointer-events-none absolute bottom-4 left-4 right-4 truncate text-[14px] leading-[14px] text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                {v.title}
              </span>
              {/* 选中白描边 4：子层最上渲染，避免被悬停渐变盖住 */}
              {i === activeIdx && (
                <span
                  className="pointer-events-none absolute inset-0 rounded-2xl"
                  style={{ border: '4px solid #FFFFFF' }}
                />
              )}
            </button>
          ))}
        </div>
        {/* 两端渐隐遮罩 180x124 + 左右箭头 36x36：仅溢出滚动模式渲染——未溢出时
            所有格已完整可见，遮罩/箭头反而遮挡边缘内容（不被裁切原则） */}
        {stripOverflow && (
          <>
            <div
              className="pointer-events-none absolute left-0 top-0 h-full w-[180px]"
              style={{ background: 'linear-gradient(90deg, #121212 0%, rgba(18, 18, 18, 0) 100%)' }}
            />
            <div
              className="pointer-events-none absolute right-0 top-0 h-full w-[180px]"
              style={{ background: 'linear-gradient(270deg, #121212 0%, rgba(18, 18, 18, 0) 100%)' }}
            />
            {/* 左右箭头 36x36 @条两端垂直居中：循环步进播放列表 */}
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="上一个"
              className="absolute left-0 top-1/2 z-10 block h-9 w-9 -translate-y-1/2 cursor-pointer transition-opacity hover:opacity-80"
            >
              <img src="/icons/featured/video-strip-arrow-left.svg" alt="" className="h-9 w-9" />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="下一个"
              className="absolute right-0 top-1/2 z-10 block h-9 w-9 -translate-y-1/2 cursor-pointer transition-opacity hover:opacity-80"
            >
              <img src="/icons/featured/video-strip-arrow-right.svg" alt="" className="h-9 w-9" />
            </button>
          </>
        )}
      </div>

      {/* 右栏折叠/展开手柄（36x36 圆角 12 #2D2D2D 白色 chevron，与图像预览页
          同款视觉与位置）：独立于右栏定位、不随右栏滑出——展开时右缘贴右栏左缘、
          top-8 与作者行齐平；收起时平滑滑到屏幕右缘，箭头方向随状态翻转
          （收起 → / 展开 ←），300ms 与右栏同步 */}
      <button
        type="button"
        aria-label={panelCollapsed ? '展开信息栏' : '收起信息栏'}
        title={panelCollapsed ? '展开信息栏' : '收起信息栏'}
        onClick={() => setPanelCollapsed((c) => !c)}
        className={`absolute top-8 z-20 block h-9 w-9 cursor-pointer transition-all duration-300 ease-out hover:opacity-80 ${
          panelCollapsed ? 'right-2' : 'right-[352px]'
        }`}
      >
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="36" height="36" rx="12" fill="#2D2D2D" />
          {panelCollapsed ? (
            <path
              d="M21 13L15 18L21 23"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <path
              d="M15 13L21 18L15 23"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </button>

      {/* 右栏 352 宽贴右、全高（设计稿 251:160：352 容器 + 288 内容 @32）——
          头像 20 / 用户名 14px / 关注 48x24（10px + 10 加号）/ 点赞 20+14px Medium /
          智能参考底 16、Sparkles 10，字号 12/12/14/12/16；flex 纵向布局——顶部（作者/
          标题）固定，描述及以下弹性滚动：描述、参数与按钮同在滚动区按文档流顺排
          （251:160：参数距描述底 16、按钮距参数 32），参数/按钮位置随描述长短
          动态变化、不贴底固定；内容超出本区高度时滚动区收缩并纵向滚动。
          用户栏在页面壳层（FeaturedPreviewPage 挂首页同款
          HomeTopBar），右栏背景上探至视口顶（顶部不预留顶栏位置，顶栏 z-[200]
          悬浮其上），栏内内容仍自顶栏下方起算——paddingTop = 14 + HOME_TOPBAR_H，
          作者行 @设计稿栏内 14 与稿完全一致 */}
      <div
        className={`absolute right-0 flex w-[352px] flex-col bg-[#181818] px-8 pb-[52px] transition-transform duration-300 ease-out ${
          panelCollapsed ? 'translate-x-full' : 'translate-x-0'
        }`}
        style={{ top: -HOME_TOPBAR_H, bottom: 0, paddingTop: 14 + HOME_TOPBAR_H }}
      >
        {/* 作者信息 @32,86 w288（栏内 14）：头像 20 + 用户名 14px + 关注钮 | 点赞 20 + 14px */}
        <div className="flex h-6 w-full shrink-0 items-center">
          <img
            src="/images/home/avatars/avatar-user.png"
            alt=""
            className="h-5 w-5 rounded-full object-cover"
          />
          <span className="ml-2 text-[14px] leading-[14px] text-white">{item.author}</span>
          <button
            type="button"
            onClick={() => setFollowed((f) => !f)}
            className="ml-3 flex h-6 w-12 cursor-pointer items-center justify-center gap-1 rounded-lg bg-[#222222] transition-colors hover:bg-[#2D2D2D]"
          >
            {!followed && (
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 2.5V9.5" stroke="white" strokeLinecap="round" />
                <path d="M2.5 6L9.5 6" stroke="white" strokeLinecap="round" />
              </svg>
            )}
            <span className={`text-[10px] leading-[10px] ${followed ? 'text-white/45' : 'text-white'}`}>{followed ? '已关注' : '关注'}</span>
          </button>
          {!item.id.startsWith('wf-') && !item.id.startsWith('v-') && (
            <button
              type="button"
              onClick={openReport}
              className="ml-2 cursor-pointer text-[10px] leading-[10px] text-[#8A8A8A] transition-colors hover:text-white"
            >
              举报
            </button>
          )}
          <div className="ml-auto flex items-center">
            <img src="/icons/home/icon-like.svg" alt="" className="h-5 w-5" />
            <span className="ml-2 text-[14px] font-medium leading-[14px] text-white">{item.likes}</span>
          </div>
        </div>

        {/* 标题 + 日期/声明 @32,140（栏内 68 = mt-[30px]：作者行底 14+24=38 → 标题锚点 68；
            12px Light 0.04em，层级靠颜色区分） */}
        <div className="mt-[30px] w-full shrink-0">
          <p className="text-[12px] font-light leading-[12px] tracking-[0.04em] text-white">{item.title}</p>
          <div className="mt-3 flex items-center text-[12px] leading-[12px] tracking-[0.04em] text-[#8A8A8A]">
            <span>{item.date}</span>
            <span className="mx-3 h-[8px] w-px bg-white/15" />
            <span>内容由AI生成</span>
          </div>
        </div>

        {/* 描述滚动区（设计稿描述 @32,208，栏内 136 = mt-8：标题区底 68+36=104 →
            描述锚点 136）：描述、参数与按钮在本区内按文档流顺排（无弹性空隙），
            参数/按钮位置随描述长短动态变化、不贴底固定；内容超出本区高度时
            纵向滚动（no-scrollbar 与左栏列表风格一致，滚轮/触控板可滚） */}
        <div className="mt-8 flex min-h-0 w-full flex-1 flex-col overflow-y-auto no-scrollbar">
          {/* 作品描述（标签 14px、正文 14px 行高 24 justified（设计稿正文 @(0,30)，
              标签底 14 → mt-4 = 16）；shrink-0 保证不被压缩，过长时由本区滚动查看 */}
          <div className="w-full shrink-0">
            <p className="text-[14px] font-light leading-[14px] text-[#8A8A8A]">作品描述</p>
            <p className="mt-4 text-[14px] leading-[24px] text-white" style={{ textAlign: 'justify' }}>
              {item.description || '暂无描述'}
            </p>
          </div>

          {/* 参数信息（设计稿 @(32,470) 270x16）：紧跟描述底 mt-4 = 16（251:160
              描述底 454 → 参数顶 470）；智能参考(16x16 底座 + 文字) | 模型 | 比例 |
              画质，文字 12px #8A8A8A 0.04em、分隔线 1x8 */}
          <div className="mt-4 flex h-4 w-full shrink-0 items-center text-[12px] leading-[12px] tracking-[0.04em] text-[#8A8A8A]">
            {item.hasSmartRef && (
              <>
                <span className="flex h-4 w-4 items-center justify-center rounded bg-[#2D2D2D]">
                  <Sparkles size={10} className="text-white/80" />
                </span>
                <span className="ml-2">智能参考</span>
                <span className="mx-3 h-2 w-px bg-white/15" />
              </>
            )}
            <span>{item.model}</span>
            <span className="mx-3 h-2 w-px bg-white/15" />
            <span>{item.aspect}</span>
            <span className="mx-3 h-2 w-px bg-white/15" />
            <span>{item.quality}</span>
          </div>

          {/* 查看制作流程（设计稿 @(32,518) 288x48 r12）：参数底 486 +32 → mt-8；
              文字 16px Medium，随文档流顺排（不贴底固定）；
              点击打开当前作品对应的只读画布项目（WorkflowPreviewPage）；
              仅发布时公开了源项目制作流程快照的作品显示（demo/模板卡/旧记录无 workflowData 不展示入口） */}
          {item.workflowData && (
            <button
              type="button"
              onClick={() => onOpenWorkflow?.(item)}
              className="mt-8 h-12 w-full shrink-0 cursor-pointer rounded-xl bg-[#2D2D2D] text-[16px] font-medium leading-[48px] text-white transition-colors hover:bg-[#3A3A3A]"
            >
              查看制作流程
            </button>
          )}
        </div>
      </div>

      {/* 全屏播放器（参考图一/二布局）：点视频悬停播放按钮进入；onSelect 同步预览页
          选中项（作品模式切换整个作品/精选模式本地切播放源，全屏内切换与预览页
          缩略图条联动）；Esc/左上返回/控制条退出按钮关闭，关闭后预览视频从原进度
          继续静音播放 */}
      {fullscreen && (
        <FullscreenVideoPlayer
          playlist={playlist}
          activeIdx={activeIdx}
          startTime={progress}
          title={item.title}
          author={item.author}
          likes={item.likes}
          onSelect={selectStrip}
          /* 全屏内点「查看制作流程」先关全屏再打开：WorkflowPreviewPage z-170
              压不过 portal 后的全屏层 z-300，不关会被盖住 */
          onOpenWorkflow={
            item.workflowData
              ? () => {
                  setFullscreen(false);
                  onOpenWorkflow?.(item);
                }
              : undefined
          }
          onClose={() => {
            setFullscreen(false);
            videoRef.current?.play().catch(() => {});
          }}
        />
      )}

      {/* 举报弹层（portal 挂 body，不受右栏/全屏播放器层叠影响） */}
      <ReportDialog open={reportOpen} workId={item.id} onClose={() => setReportOpen(false)} />
    </div>
  );
};
